import React from 'react'
import { Upload, Progress, Typography, List, Tag, Spin, Tooltip, Modal, Button, Alert, Badge } from 'antd'
import {
  InboxOutlined,
  FileOutlined,
  DeleteOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  CaretUpOutlined,
  CaretDownOutlined,
} from '@ant-design/icons'
import PropTypes from 'prop-types'
import './styles/FileUpload.scss'

const { Text } = Typography
const { Dragger } = Upload

const MODAL_TEXT = {
  title: 'CSV File Validation',
  titleSuccess: 'File Validation Successful',
  titleError: 'CSV File Has Errors',
  titleWarning: 'CSV File Has Warnings',
  descriptionSuccess: 'Your CSV file has been validated successfully and is ready for processing.',
  descriptionError: 'Your file has validation issues that need to be fixed before proceeding.',
  descriptionWarning: 'Your file has column warnings that may affect processing.',
  viewErrors: 'View Validation Errors',
  reviewWarnings: 'Review Warnings',
}

const FileUpload = ({
  onFileUpload,
  onUploadChange,
  isProcessing,
  progress,
  displayProgress,
  showProgress,
  timeRemaining,
  formatTimeRemaining,
  disabled,
  fileName,
  fileValidation,
  showValidation,
  onRemoveFile,
  hasFieldsSelected,
  isValidationModalVisible,
  onOpenValidationModal,
  onCloseValidationModal,
  expandedSections,
  onToggleSection,
}) => {
  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.csv',
    maxCount: 1,
    disabled,
    beforeUpload: (file) => {
      onFileUpload(file, { action: 'upload' })
      return false
    },
    showUploadList: false,
    onChange: (info) => {
      onUploadChange(info)
    },
    action: 'javascript:void(0)',
    customRequest: ({ onSuccess }) => {
      setTimeout(() => {
        onSuccess('ok', null)
      }, 0)
    },
  }

  const getUploadText = () => {
    if (fileName && !isProcessing && fileValidation?.isValidationComplete) {
      return 'Upload Complete'
    }
    return 'Click or drag CSV file to this area to upload'
  }

  // Determine if spinner should be shown
  const shouldShowSpinner =
    isProcessing || (progress > 0 && progress < 100) || (progress === 100 && displayProgress < 100)

  // Helper to visualize only the problematic whitespace
  const visualizeWhitespace = (text, group) => {
    if (!text) {
      return '<empty>'
    }

    const normalizedText = text.trim()
    if (text === normalizedText) {
      return text
    }

    const elements = []
    let inLeadingSpace = true

    for (let i = 0; i < text.length; i++) {
      const char = text[i]

      if (inLeadingSpace && char === ' ') {
        elements.push(
          <span key={`ws-${i}`} className="ws-highlight">
            ·
          </span>,
        )
      } else if (inLeadingSpace && char !== ' ') {
        inLeadingSpace = false
        elements.push(char)
      } else if (!inLeadingSpace && i >= normalizedText.length + text.indexOf(normalizedText[0]) && char === ' ') {
        elements.push(
          <span key={`ws-${i}`} className="ws-highlight">
            ·
          </span>,
        )
      } else {
        elements.push(char)
      }
    }

    return <>{elements}</>
  }

  // Group duplicate column tags to improve legibility
  const renderDuplicateColumnTags = (duplicateColumns) => {
    // Map to store column tags by their normalized version (lowercase + trimmed)
    const normalizedGroups = new Map()

    // Group columns by their normalized form
    duplicateColumns.forEach((column) => {
      const normalized = column.trim().toLowerCase()
      if (!normalizedGroups.has(normalized)) {
        normalizedGroups.set(normalized, [])
      }
      normalizedGroups.get(normalized).push(column)
    })

    // Convert the groups to an array for rendering
    const groupedDuplicates = Array.from(normalizedGroups.values())

    return (
      <div className="duplicate-columns-group">
        {groupedDuplicates.map((group, index) => (
          <div key={index} className="duplicate-group">
            {group.map((column) => (
              <Tooltip
                key={column}
                title={column !== column.trim() ? `"${column}" (contains leading/trailing whitespace)` : null}
              >
                <Tag className={column !== column.trim() ? 'whitespace-tag' : ''} color="error">
                  {visualizeWhitespace(column, group)}
                </Tag>
              </Tooltip>
            ))}
          </div>
        ))}
      </div>
    )
  }

  // Helper to highlight commas in the line content
  const highlightCommas = (text) => {
    if (!text) {
      return '<empty>'
    }

    // Split the text by commas and preserve them in the output
    return text.split(',').map((part, index, array) => {
      // Don't add comma after the last part
      if (index === array.length - 1) {
        return (
          <span key={index} className="content-segment">
            {part}
          </span>
        )
      }

      // Add highlighted comma between parts
      return (
        <React.Fragment key={index}>
          <span className="content-segment">{part}</span>
          <span className="highlighted-comma">,</span>
        </React.Fragment>
      )
    })
  }

  // Modify the renderCommaValidationErrors function to only show first row example
  const renderCommaValidationErrors = (invalidRows) => {
    if (!invalidRows || !invalidRows.length) {
      return null
    }

    // Only show the first row as an example
    const exampleRow = invalidRows[0]

    // Create a comma-separated list of row numbers for easy selection
    const rowNumbers = invalidRows.map((row) => row.rowNumber).join(', ')

    return (
      <div className="comma-validation-errors">
        <div className="invalid-row-example">
          <div className="row-header">
            <div className="comma-count">
              <Text strong>Expected:</Text> <Text className="count-value">{exampleRow.expectedCommas}</Text>
              <Text strong>Found:</Text> <Text className="count-value error">{exampleRow.actualCommas}</Text>
            </div>
          </div>
          <div className="row-content">
            <code className="row-text">{highlightCommas(exampleRow.lineContent)}</code>
          </div>
        </div>

        <div className="affected-rows-container">
          <Text strong>Affected rows</Text>
          <div className="affected-rows-list">
            <Tooltip title="Click to select all row numbers">
              <code className="row-numbers">{rowNumbers}</code>
            </Tooltip>
          </div>
        </div>
      </div>
    )
  }

  // Determine validation status
  const getValidationStatus = () => {
    if (!fileName || !fileValidation) {
      return null
    }

    const hasErrors =
      fileValidation.missingColumns?.length > 0 ||
      fileValidation.duplicateColumns?.length > 0 ||
      fileValidation.invalidCommaRows?.length > 0

    const hasWarnings = Array.isArray(fileValidation.extraColumns) && fileValidation.extraColumns.length > 0

    if (hasErrors) {
      return 'error'
    }
    if (hasWarnings) {
      return 'warning'
    }
    return 'success'
  }

  // Get count of validation issues
  const getValidationIssueCount = () => {
    if (!fileValidation) {
      return 0
    }

    return (
      (fileValidation.missingColumns?.length || 0) +
      (fileValidation.duplicateColumns?.length || 0) +
      (fileValidation.invalidCommaRows?.length || 0) +
      (fileValidation.extraColumns?.length || 0)
    )
  }

  // Summary for validation status
  const getValidationSummary = () => {
    if (!fileName || !showValidation || !fileValidation) {
      return null
    }

    const status = getValidationStatus()
    if (!status) {
      return null
    }

    const issueCount = getValidationIssueCount()

    if (status === 'success') {
      return (
        <Alert
          message={MODAL_TEXT.titleSuccess}
          description={MODAL_TEXT.descriptionSuccess}
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          className="validation-summary"
        />
      )
    }

    if (status === 'error') {
      return (
        <div className="validation-summary-panel error">
          <div className="validation-summary-content">
            <div className="validation-summary-icon">
              <CloseCircleOutlined />
            </div>
            <div className="validation-summary-text">
              <div className="validation-summary-title">
                {MODAL_TEXT.titleError}
                <Badge
                  count={issueCount}
                  className="validation-badge"
                  style={{ backgroundColor: '#ff4d4f', marginLeft: '8px' }}
                />
              </div>
              <div className="validation-summary-description">{MODAL_TEXT.descriptionError}</div>
              <div className="validation-summary-action">
                <Button
                  type="primary"
                  danger
                  onClick={() => {
                    onOpenValidationModal()
                  }}
                  size="middle"
                  icon={<ExclamationCircleOutlined />}
                >
                  {MODAL_TEXT.viewErrors}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (status === 'warning') {
      return (
        <div className="validation-summary-panel warning">
          <div className="validation-summary-content">
            <div className="validation-summary-icon">
              <WarningOutlined />
            </div>
            <div className="validation-summary-text">
              <div className="validation-summary-title">
                {MODAL_TEXT.titleWarning}
                <Badge
                  count={issueCount}
                  className="validation-badge"
                  style={{ backgroundColor: '#faad14', marginLeft: '8px' }}
                />
              </div>
              <div className="validation-summary-description">{MODAL_TEXT.descriptionWarning}</div>
              <div className="validation-summary-action">
                <Button type="primary" onClick={onOpenValidationModal} size="middle" icon={<InfoCircleOutlined />}>
                  {MODAL_TEXT.reviewWarnings}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return null
  }

  // Render validation details in modal - ensure all error types are displayed
  const renderValidationDetails = () => {
    if (!fileValidation) {
      return null
    }

    const hasInvalidCommas = fileValidation.invalidCommaRows?.length > 0
    const hasDuplicateColumns = fileValidation.duplicateColumns?.length > 0
    const hasMissingColumns = fileValidation.missingColumns?.length > 0
    const hasExtraColumns = fileValidation.extraColumns?.length > 0

    // If no validation issues, show a success message
    if (!hasInvalidCommas && !hasDuplicateColumns && !hasMissingColumns && !hasExtraColumns) {
      return (
        <div className="validation-success">
          <CheckCircleOutlined className="success-icon" />
          <Text>{MODAL_TEXT.descriptionSuccess}</Text>
        </div>
      )
    }

    // Get counts of each validation issue type
    const issueCount = {
      commaErrors: fileValidation.invalidCommaRows?.length || 0,
      duplicateColumns: fileValidation.duplicateColumns?.length || 0,
      missingColumns: fileValidation.missingColumns?.length || 0,
      extraColumns: fileValidation.extraColumns?.length || 0,
    }

    return (
      <div className="validation-details">
        <div className="validation-summary-stats">
          <div className="issue-type-counts">
            <div className="issue-badge-container">
              <Badge
                count={issueCount.commaErrors}
                className={`issue-badge ${issueCount.commaErrors > 0 ? 'error' : 'zero'}`}
                showZero
              />
              <Tag color={issueCount.commaErrors > 0 ? 'error' : 'default'} className="issue-type-tag">
                Row Structure
              </Tag>
            </div>
            <div className="issue-badge-container">
              <Badge
                count={issueCount.duplicateColumns}
                className={`issue-badge ${issueCount.duplicateColumns > 0 ? 'error' : 'zero'}`}
                showZero
              />
              <Tag color={issueCount.duplicateColumns > 0 ? 'error' : 'default'} className="issue-type-tag">
                Duplicate Columns
              </Tag>
            </div>
            <div className="issue-badge-container">
              <Badge
                count={issueCount.missingColumns}
                className={`issue-badge ${issueCount.missingColumns > 0 ? 'error' : 'zero'}`}
                showZero
              />
              <Tag color={issueCount.missingColumns > 0 ? 'error' : 'default'} className="issue-type-tag">
                Missing Columns
              </Tag>
            </div>
            <div className="issue-badge-container">
              <Badge
                count={issueCount.extraColumns}
                className={`issue-badge ${issueCount.extraColumns > 0 ? 'warning' : 'zero'}`}
                showZero
              />
              <Tag color={issueCount.extraColumns > 0 ? 'warning' : 'default'} className="issue-type-tag">
                Extra Columns
              </Tag>
            </div>
          </div>
        </div>

        {/* Show comma validation errors first since they're most critical */}
        {hasInvalidCommas && (
          <div className="validation-error">
            <div
              className={`validation-header ${expandedSections.commaErrors ? 'expanded' : 'collapsed'}`}
              onClick={() => onToggleSection('commaErrors')}
            >
              <ExclamationCircleOutlined />
              <div className="header-text-container">
                <Text strong>Mismatched CSV Row Structure</Text>
                <Badge count={issueCount.commaErrors} className="issue-count" />
              </div>
              <div className="collapse-icon">
                {expandedSections.commaErrors ? <CaretUpOutlined /> : <CaretDownOutlined />}
              </div>
            </div>
            {expandedSections.commaErrors && (
              <div className="validation-content">
                <Alert
                  type="error"
                  message="Each row must have the same number of commas as the header row"
                  description="Your CSV file contains rows with inconsistent comma counts, which will cause data to be misaligned."
                />
                {renderCommaValidationErrors(fileValidation.invalidCommaRows)}
                <div className="error-action">
                  <Text type="danger">
                    Please correct your CSV file and upload again. Ensure all rows have the same number of columns.
                  </Text>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Show duplicate columns */}
        {hasDuplicateColumns && (
          <div className="validation-error">
            <div
              className={`validation-header ${expandedSections.duplicateColumns ? 'expanded' : 'collapsed'}`}
              onClick={() => onToggleSection('duplicateColumns')}
            >
              <CloseCircleOutlined />
              <div className="header-text-container">
                <Text strong>Duplicate Column Headers Found</Text>
                <Badge count={issueCount.duplicateColumns} className="issue-count" />
              </div>
              <div className="collapse-icon">
                {expandedSections.duplicateColumns ? <CaretUpOutlined /> : <CaretDownOutlined />}
              </div>
            </div>
            {expandedSections.duplicateColumns && (
              <div className="validation-content">
                <Alert
                  type="error"
                  message="Each column must have a unique name"
                  description="Your CSV file contains duplicate column headers. Headers are considered duplicates even if they differ only by case or whitespace."
                />
                <div className="validation-tags">{renderDuplicateColumnTags(fileValidation.duplicateColumns)}</div>
                <Text type="secondary" className="whitespace-legend">
                  <span className="legend-dot">·</span> represents a space character
                </Text>
              </div>
            )}
          </div>
        )}

        {/* Show missing columns */}
        {hasMissingColumns && (
          <div className="validation-error">
            <div
              className={`validation-header ${expandedSections.missingColumns ? 'expanded' : 'collapsed'}`}
              onClick={() => onToggleSection('missingColumns')}
            >
              <WarningOutlined />
              <div className="header-text-container">
                <Text strong>Missing Required Columns</Text>
                <Badge count={issueCount.missingColumns} className="issue-count" />
              </div>
              <div className="collapse-icon">
                {expandedSections.missingColumns ? <CaretUpOutlined /> : <CaretDownOutlined />}
              </div>
            </div>
            {expandedSections.missingColumns && (
              <div className="validation-content">
                <Alert
                  type="error"
                  message="Required columns are missing"
                  description="Your CSV file is missing columns that are required for processing."
                />
                <div className="validation-tags">
                  {fileValidation.missingColumns.map((column) => (
                    <Tag key={column} color="error">
                      {column}
                    </Tag>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Show extra columns (these are warnings, not errors) */}
        {hasExtraColumns && (
          <div className="validation-warning">
            <div
              className={`validation-header ${expandedSections.extraColumns ? 'expanded' : 'collapsed'}`}
              onClick={() => onToggleSection('extraColumns')}
            >
              <InfoCircleOutlined />
              <div className="header-text-container">
                <Text strong>Additional Columns Found</Text>
                <Badge count={issueCount.extraColumns} className="issue-count" />
              </div>
              <div className="collapse-icon">
                {expandedSections.extraColumns ? <CaretUpOutlined /> : <CaretDownOutlined />}
              </div>
            </div>
            {expandedSections.extraColumns && (
              <div className="validation-content">
                <Alert
                  type="warning"
                  message="Extra columns detected"
                  description="Extra columns may cause data misalignment. Consider removing extra columns or review changes in the next step."
                />
                <div className="validation-tags">
                  {fileValidation.extraColumns.length > 5 ? (
                    <Text type="warning">{`${fileValidation.extraColumns.length} additional columns found`}</Text>
                  ) : (
                    fileValidation.extraColumns.map((column) => (
                      <Tag key={column} color="warning">
                        {column}
                      </Tag>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  // Validation modal
  const renderValidationModal = () => {
    // Determine validation status for styling
    const status = getValidationStatus()
    let modalClassStatus = ''
    let modalTitle = MODAL_TEXT.title

    if (status === 'error') {
      modalClassStatus = 'error-modal'
      modalTitle = MODAL_TEXT.titleError
    } else if (status === 'warning') {
      modalClassStatus = 'warning-modal'
      modalTitle = MODAL_TEXT.titleWarning
    }

    return (
      <Modal
        title={
          <div className="validation-modal-title">
            <span>{modalTitle}</span>
          </div>
        }
        visible={isValidationModalVisible}
        onCancel={onCloseValidationModal}
        footer={[
          <Button key="close" onClick={onCloseValidationModal} size="large">
            Close
          </Button>,
        ]}
        width={700}
        className={`validation-modal ${modalClassStatus}`}
        destroyOnClose={true}
        bodyStyle={{ maxHeight: '70vh', overflowY: 'auto', padding: '0', borderRadius: '0 0 8px 8px' }}
      >
        {renderValidationDetails()}
      </Modal>
    )
  }

  return (
    <>
      <div className="file-upload-container">
        <div className="upload-section">
          <div className="upload-main">
            {!hasFieldsSelected ? (
              <Tooltip
                title="Select at least one field to override before uploading a file"
                placement="topRight"
                overlayClassName="upload-hint-tooltip"
              >
                <div>
                  <Dragger {...uploadProps} className="file-upload-dragger" disabled={true}>
                    {shouldShowSpinner ? (
                      <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    ) : (
                      <>
                        <p className="upload-icon upload-disabled">
                          <InboxOutlined />
                        </p>
                        <p className="upload-text upload-disabled">{getUploadText()}</p>
                      </>
                    )}
                  </Dragger>
                </div>
              </Tooltip>
            ) : (
              <Dragger {...uploadProps} className="file-upload-dragger">
                {shouldShowSpinner ? (
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                ) : (
                  <>
                    <p className="upload-icon">
                      <InboxOutlined />
                    </p>
                    <p className="upload-text">{getUploadText()}</p>
                  </>
                )}
              </Dragger>
            )}

            <div className="file-list">
              {fileName ? (
                <List
                  itemLayout="horizontal"
                  dataSource={[{ name: fileName }]}
                  renderItem={(item) => (
                    <List.Item
                      className="file-list-item"
                      actions={[<DeleteOutlined key="delete" onClick={onRemoveFile} className="delete-icon" />]}
                    >
                      <div className="file-item-content">
                        <div className="file-info">
                          <FileOutlined className="file-icon" />
                          <Text ellipsis className="file-name">
                            {item.name}
                          </Text>
                        </div>
                        {showProgress && (
                          <div className="progress-wrapper">
                            <Progress
                              percent={Math.round(displayProgress)}
                              size="small"
                              status={isProcessing ? 'active' : 'success'}
                            />
                          </div>
                        )}
                      </div>
                    </List.Item>
                  )}
                />
              ) : (
                <div className="file-list-placeholder">
                  <Text type="secondary">No file selected</Text>
                </div>
              )}
            </div>
          </div>

          {/* Validation summary side panel */}
          <div className="validation-summary-side">{getValidationSummary()}</div>
        </div>
      </div>

      {/* Validation details modal */}
      {renderValidationModal()}

      {showProgress && isProcessing && timeRemaining && (
        <div className="time-remaining">
          <Text className="time-text">{formatTimeRemaining(timeRemaining)}</Text>
        </div>
      )}
    </>
  )
}

FileUpload.propTypes = {
  onFileUpload: PropTypes.func.isRequired,
  onUploadChange: PropTypes.func.isRequired,
  isProcessing: PropTypes.bool,
  progress: PropTypes.number,
  displayProgress: PropTypes.number,
  showProgress: PropTypes.bool,
  timeRemaining: PropTypes.number,
  formatTimeRemaining: PropTypes.func,
  disabled: PropTypes.bool,
  fileName: PropTypes.string,
  fileValidation: PropTypes.shape({
    missingColumns: PropTypes.arrayOf(PropTypes.string),
    extraColumns: PropTypes.arrayOf(PropTypes.string),
    duplicateColumns: PropTypes.arrayOf(PropTypes.string),
    invalidCommaRows: PropTypes.arrayOf(
      PropTypes.shape({
        rowNumber: PropTypes.number,
        lineContent: PropTypes.string,
        expectedCommas: PropTypes.number,
        actualCommas: PropTypes.number,
      }),
    ),
    isValid: PropTypes.bool,
  }).isRequired,
  showValidation: PropTypes.bool,
  onRemoveFile: PropTypes.func.isRequired,
  hasFieldsSelected: PropTypes.bool,
  isValidationModalVisible: PropTypes.bool,
  onOpenValidationModal: PropTypes.func,
  onCloseValidationModal: PropTypes.func,
  expandedSections: PropTypes.shape({
    commaErrors: PropTypes.bool,
    duplicateColumns: PropTypes.bool,
    missingColumns: PropTypes.bool,
    extraColumns: PropTypes.bool,
  }),
  onToggleSection: PropTypes.func,
}

FileUpload.defaultProps = {
  onFileUpload: () => {},
  onUploadChange: () => {},
  isProcessing: false,
  progress: 0,
  displayProgress: 0,
  showProgress: false,
  timeRemaining: null,
  formatTimeRemaining: (seconds) => `${Math.round(seconds)}s remaining`,
  disabled: true,
  fileName: '',
  fileValidation: {
    missingColumns: [],
    extraColumns: [],
    duplicateColumns: [],
    invalidCommaRows: [],
    isValid: true,
  },
  showValidation: false,
  onRemoveFile: () => {},
  hasFieldsSelected: false,
  isValidationModalVisible: false,
  onOpenValidationModal: () => {},
  onCloseValidationModal: () => {},
  expandedSections: {
    commaErrors: false,
    duplicateColumns: false,
    missingColumns: false,
    extraColumns: false,
  },
  onToggleSection: () => {},
}

export default FileUpload
